/**
 * Workbook Analyzer Panel Component
 * Analyzes entire workbook for errors and performance issues
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorDisplay } from '../common/ErrorDisplay';
import type { WorkbookAnalysisReport } from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';

interface WorkbookAnalyzerPanelProps {
  onAnalysisComplete?: (report: WorkbookAnalysisReport) => void;
}

export const WorkbookAnalyzerPanel: React.FC<WorkbookAnalyzerPanelProps> = ({
  onAnalysisComplete,
}) => {
  const [report, setReport] = useState<WorkbookAnalysisReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary']));

  const analyzeWorkbook = async () => {
    setError(null);
    setIsLoading(true);

    try {
      // Read real workbook data from Excel
      let workbookData: { sheets: Array<{ name: string; data: unknown[][]; formulas: string[] }> } = { sheets: [] };

      await Excel.run(async (ctx) => {
        const sheets = ctx.workbook.worksheets;
        sheets.load('items/name');
        await ctx.sync();

        for (const sheet of sheets.items) {
          const usedRange = sheet.getUsedRangeOrNullObject();
          usedRange.load(['values', 'formulas', 'address']);
          await ctx.sync();

          if (usedRange.isNullObject) {
            workbookData.sheets.push({ name: sheet.name, data: [], formulas: [] });
            continue;
          }

          const values = usedRange.values as unknown[][];
          const formulas = usedRange.formulas as string[][];

          // Flatten formulas: keep only cells that start with '='
          const flatFormulas: string[] = [];
          for (const row of formulas) {
            for (const cell of row) {
              if (typeof cell === 'string' && cell.startsWith('=')) {
                flatFormulas.push(cell);
              }
            }
          }

          workbookData.sheets.push({ name: sheet.name, data: values, formulas: flatFormulas });
        }
      });

      const response = await fetch('/api/workbook-analyzer/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(workbookData),
      });

      if (!response.ok) {
        throw new Error(`Erreur API : ${response.statusText}`);
      }

      const analysisReport: WorkbookAnalysisReport = await response.json();
      setReport(analysisReport);

      if (onAnalysisComplete) {
        onAnalysisComplete(analysisReport);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(`Impossible d'analyser le classeur : ${msg}`);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getHealthColor = (score: number): string => {
    if (score >= 80) return '#4CAF50';
    if (score >= 50) return '#FF9800';
    return '#F44336';
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#F44336';
      case 'warning': return '#FF9800';
      case 'info': return '#2196F3';
      default: return '#999';
    }
  };

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>🔍 Analyseur de classeur</h3>
        <p className="preview-subtitle">Analysez la santé et les performances de votre classeur</p>
      </div>

      {error && <ErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {!report ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ marginBottom: '16px', color: '#666' }}>
            Analysez l'intégralité de votre classeur pour détecter les erreurs, problèmes de performance et bonnes pratiques.
          </p>
          <button
            onClick={analyzeWorkbook}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Analyse en cours...' : 'Analyser le classeur'}
          </button>
          {isLoading && <LoadingSpinner label="Analyse du classeur en cours..." />}
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          {/* Résumé */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => toggleSection('summary')}
              style={{
                width: '100%', padding: '12px', backgroundColor: '#f5f5f5',
                border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer',
                textAlign: 'left', fontWeight: 'bold', display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <span>📊 Résumé</span>
              <span>{expandedSections.has('summary') ? '▼' : '▶'}</span>
            </button>

            {expandedSections.has('summary') && (
              <div style={{ padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Score de santé</p>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: getHealthColor(report.summary.healthScore) }}>
                      {report.summary.healthScore}/100
                    </div>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Problèmes totaux</p>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F44336' }}>{report.summary.totalIssues}</div>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Erreurs critiques</p>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#F44336' }}>{report.summary.criticalIssues}</div>
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>Avertissements</p>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#FF9800' }}>{report.summary.warningCount}</div>
                  </div>
                </div>
                <p style={{ fontSize: '12px', color: '#999', margin: '0' }}>
                  Feuilles analysées : {report.sheets} | Formules : {report.formulas}
                </p>
                <p style={{ fontSize: '12px', color: '#999', margin: '4px 0 0 0' }}>
                  Dernière analyse : {new Date(report.summary.lastAnalyzed).toLocaleString('fr-FR')}
                </p>
              </div>
            )}
          </div>

          {/* Erreurs */}
          {report.errors.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => toggleSection('errors')}
                style={{
                  width: '100%', padding: '12px', backgroundColor: '#ffebee',
                  border: '1px solid #ffcdd2', borderRadius: '4px', cursor: 'pointer',
                  textAlign: 'left', fontWeight: 'bold', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span>❌ Erreurs ({report.errors.length})</span>
                <span>{expandedSections.has('errors') ? '▼' : '▶'}</span>
              </button>

              {expandedSections.has('errors') && (
                <div style={{ padding: '12px', backgroundColor: '#fff5f5', borderRadius: '4px' }}>
                  {report.errors.map((err, idx) => (
                    <div key={idx} style={{
                      marginBottom: '8px', padding: '8px', backgroundColor: 'white',
                      borderLeft: `4px solid ${getSeverityColor(err.severity)}`, borderRadius: '2px',
                    }}>
                      <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '13px' }}>{err.type}</p>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>{err.description}</p>
                      {err.suggestion && (
                        <p style={{ margin: '0', fontSize: '12px', color: '#2196F3' }}>💡 {err.suggestion}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Avertissements de performance */}
          {report.performanceWarnings.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => toggleSection('warnings')}
                style={{
                  width: '100%', padding: '12px', backgroundColor: '#fff3e0',
                  border: '1px solid #ffe0b2', borderRadius: '4px', cursor: 'pointer',
                  textAlign: 'left', fontWeight: 'bold', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span>⚠️ Avertissements de performance ({report.performanceWarnings.length})</span>
                <span>{expandedSections.has('warnings') ? '▼' : '▶'}</span>
              </button>

              {expandedSections.has('warnings') && (
                <div style={{ padding: '12px', backgroundColor: '#fffbf0', borderRadius: '4px' }}>
                  {report.performanceWarnings.map((warning, idx) => (
                    <div key={idx} style={{
                      marginBottom: '8px', padding: '8px', backgroundColor: 'white',
                      borderLeft: `4px solid ${getSeverityColor(warning.impact)}`, borderRadius: '2px',
                    }}>
                      <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '13px' }}>{warning.type}</p>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>{warning.description}</p>
                      {warning.recommendation && (
                        <p style={{ margin: '0', fontSize: '12px', color: '#2196F3' }}>💡 {warning.recommendation}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Suggestions */}
          {report.suggestions.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <button
                onClick={() => toggleSection('suggestions')}
                style={{
                  width: '100%', padding: '12px', backgroundColor: '#e3f2fd',
                  border: '1px solid #bbdefb', borderRadius: '4px', cursor: 'pointer',
                  textAlign: 'left', fontWeight: 'bold', display: 'flex',
                  justifyContent: 'space-between', alignItems: 'center',
                }}
              >
                <span>💡 Suggestions ({report.suggestions.length})</span>
                <span>{expandedSections.has('suggestions') ? '▼' : '▶'}</span>
              </button>

              {expandedSections.has('suggestions') && (
                <div style={{ padding: '12px', backgroundColor: '#f5f9ff', borderRadius: '4px' }}>
                  {report.suggestions.map((suggestion, idx) => (
                    <div key={idx} style={{
                      marginBottom: '8px', padding: '8px', backgroundColor: 'white',
                      borderLeft: '4px solid #2196F3', borderRadius: '2px',
                    }}>
                      <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '13px' }}>{suggestion.title}</p>
                      <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#666' }}>{suggestion.description}</p>
                      {suggestion.action && (
                        <p style={{ margin: '0', fontSize: '12px', color: '#2196F3' }}>→ {suggestion.action}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            onClick={analyzeWorkbook}
            disabled={isLoading}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#2196F3', color: 'white',
              border: 'none', borderRadius: '4px', cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1, marginTop: '12px',
            }}
          >
            {isLoading ? 'Analyse en cours...' : '🔄 Ré-analyser'}
          </button>
        </div>
      )}
    </div>
  );
};
